/**
 * Mobile remote bootstrap — controls-only, no portal auth.
 */
import { WsClient, type WsConnectionStatus } from "./ws-client";

export type RemoteCallbacks = {
  onStatus: (status: WsConnectionStatus, attempt: number) => void;
  onPlayingChange: (playing: boolean) => void;
  onHostGone: () => void;
  onClosed: () => void;
};

let client: WsClient | null = null;
let applyingRemote = false;

export function connectRemote(code: string, callbacks: RemoteCallbacks): WsClient {
  client?.disconnect();
  client = new WsClient();

  client.onStatus(callbacks.onStatus);

  client.on("sync", (msg) => {
    applyingRemote = true;
    if (typeof msg.isPaid === "boolean") {
      window.__EMDR_IS_PAID__ = msg.isPaid;
      window.__EMDR_REFRESH_PAID_UI__?.();
    }
    if (msg.data && typeof msg.data === "object") {
      window.__EMDR_APPLY_REMOTE_STATE__?.(msg.data);
    }
    applyingRemote = false;
    if (typeof msg.playing === "boolean") {
      callbacks.onPlayingChange(msg.playing);
    }
  });

  client.on("play", (msg) => {
    if (typeof msg.playing === "boolean") {
      callbacks.onPlayingChange(msg.playing);
    }
  });

  client.on("host-gone", () => {
    callbacks.onHostGone();
  });

  client.on("connected", () => {
    // host acknowledged remote join
  });

  window.__EMDR_ON_STATE_CHANGE__ = () => {
    if (applyingRemote) return;
    const data = window.__EMDR_GET_STATE__?.() ?? {};
    client?.send({ type: "state", data });
  };

  client.connect(code, "remote");
  return client;
}

export function sendPlay(playing: boolean): void {
  client?.send({ type: "play", playing });
}

export function sendRemoteExit(): void {
  client?.send({ type: "remote-exit" });
  client?.disconnect();
  client = null;
  window.__EMDR_ON_STATE_CHANGE__ = undefined;
}

export function disconnectRemote(): void {
  client?.disconnect();
  client = null;
  window.__EMDR_ON_STATE_CHANGE__ = undefined;
}

export function getRemoteClient(): WsClient | null {
  return client;
}

export function bootstrapRemoteEmdr(options: {
  isPaid: boolean;
  onPaywall?: (feature: string) => void;
}): Promise<void> {
  window.__EMDR_REMOTE_MODE__ = true;
  window.__EMDR_IS_PAID__ = options.isPaid;
  window.__EMDR_ON_PAYWALL__ = options.onPaywall || (() => {});

  return import("./figures").then(() =>
    import("./experimental").then(() =>
      import("./emdr-core").then(({ runEmdrInit }) => {
        runEmdrInit();
      })
    )
  );
}
